import { Comparator } from '@core/utils/sort/sortBy';
import { TimeFilter } from '@shared/timeFilter/types';
import {
    NavigationDestination,
    NavigationItem,
} from '@app/router/types';
import {
    GamingRoutes,
    Site,
    NavigationContext,
    Locale,
} from '@superbet-group/web.types';
import OfferEvent from '@core/models/offer/OfferEvent';
import { CustomGamingRoutes } from '@gamingFederation/customRoutes';
import {
    DepositType,
    WithdrawalType,
} from '@core/models/user/enums';
import { BonusSubtypeDto } from '@src/app/promotionHub/bonuses/types/betlerDto/BonusSubtypeDto';
import { BonusTypeComtrade } from '@src/app/promotionHub/bonuses/types/BonusTypeDto';
import { AccountSidebarItem } from '@modules/userProfile/sidebarItems';
import { SettingsItem } from '@accountFederation/modules/settings/settingsItems';
import { PluralizationResolver } from '@sportOfferFederation/i18n/translate';
import { PromotionHubTab } from '@src/app/promotionHub/shared/types/promotionHubTab';

export type ShellLocalConfig = {
    responsibleGamingPageElements: Record<ResponsibleGamingPageElement, boolean>;
    accountSettingsWhitelist?: AccountSidebarItem[]
    settingsMenuWhitelist?: SettingsItem[];
    contactChannelsEnabled: boolean;
    footer: Footer;
    header: Header;
    bonusWallet: BonusWallet;
    showFooterRightsReserved: boolean;
    hasLimitsConsent: boolean;
    countryCodesWhitelist: string[];
    getAccountSidebarMenuItems: () => MenuItem[];
    hasImprovedWithdrawalError: boolean;
    hasImprovedDepositError: boolean;
    isUsingErrorCodesTranslations: boolean;
    isUsingHistoryFrontendTranslations: boolean;
    isSportTaxVisible: boolean;
    hasFeatureToggleService: boolean;
    hasSportsFederation: boolean;
    hasGamingFederation: boolean;
    hasTicketsFederation: boolean;
    hasShopsFederation: boolean;
    hasSocialFederation: boolean;
    deposit: Deposit;
    withdrawal: Withdrawal;
    hasEnforcedAmountDecimals: boolean;
    sseSentryDsn: string;
    logo: Logo;
    accountRecovery: boolean;
    superSpinGameLaunch?: boolean;
    seonConfig: SeonConfig;
    bankAccount?: BankAccountConfig;
    hasResponsibleGamingClosureReason: boolean;
    depositLimit: DepositLimit;
    lossLimit: LossLimit,
    sessionLimit: SessionLimit,
    defaultSportTimeFilter: TimeFilter;
    defaultTopTenTimeFilter: TimeFilter[];
    defaultPromotionsTimeFilter: TimeFilter[];
    sportOfferSort: SportOfferSort;
    // for cases where we want to fetch English offer to display but still have other Locale for something else, e.g. routes
    offerLocaleOverride: null | Locale,
    isTimeOutCustomPeriod: boolean;
    hasMultipleDomains: boolean;
    siteLanguageConfig:
        | SingleLingual
        | MultiLingual;
    urlLocale: Locale;
    sites: Site[];
    navigationDestinations: Partial<Record<NavigationItem, NavigationDestination>>;
    bottomNavbarItems: NavigationItem[];
    bonus: Bonus;
    hasTicketsOutsideSportsProduct: boolean;
    isCompleteAccountActivationModalEnabled: boolean;
    shouldUseUserUuidForInbox: boolean;
    shouldShowSessionExpireModal: boolean;
    isEmailContactShown: boolean;
    footerContactFormUrl: string | null;
    myCasinoRouteNames: Array<GamingRoutes | CustomGamingRoutes>;
    hasCoinShopAccountMenuLink: boolean;
    hasResponsibleGamingAccountMenuLinks: boolean;
    hasDepositAndWithdrawalAccountMenuLinks: boolean;
    hasLicencePreferencesAccountMenuLink: boolean;
    hasDepositHeaderLink: boolean;
    isHeaderHelpIcon: boolean;
    responsibleGamblingInfoUrl?: Partial<Record<Locale, string>>;
    responsibleGamblingLoginModal?: string;
    qrDeposit?: {
        durationInMinutes: number;
        qrCodeSize: number;
        qrCodeFillColor: string;
        qrCodeBackgroundColor: string;
        ecLevel: 'H' | 'Q' | 'M' | 'L';
        shouldShowChangePaymentButton: boolean;
        shouldShowNumber: boolean;
        shouldShowCopyButton: boolean;
        shouldShowRequestedAmount: boolean;
        shouldShowModalOnCompletion: boolean;
    };
    freeToPlay: FreeToPlay;
    isScreenOpenAccountOpenSent?: boolean;
    responsibleGamingHasExtraClosureInfo?: boolean;
    arePromotedEventsEnabled: boolean;
    shouldShowModalsOnResponsibleGamingPage: boolean;
    neutralNavigationContext?: NavigationContext;
    defaultDepositTypes: Record<DepositType, boolean>;
    defaultWithdrawalTypes: Record<WithdrawalType, boolean>;
    playerAccountErrorFaqUrl: string;
    isBetBuilderNewBadgeShown: boolean;
    help: Help;
    isSportalNewsEnabled: boolean;
    hasSponsorshipTitle: boolean;
    hasSponsorshipSubtitle: boolean;
    isMexico?: boolean;
    isUsernameDisplayed: boolean;
    failedLoginAttemptsBeforeForgotPassword: number;
    isMultiLoginTarget: boolean;
    sportTranslationsUrl: string;
    hasBalance: boolean;
    pluralizationMethod?: PluralizationResolver;
    hasSelfExclusionWarning: boolean;
    isGeolocsScheduledPingEnabled: boolean;
    shouldPromptKycOnLogin?: boolean;
    isOnlyKycPendingStatus?: boolean;
    amplitudeServerZone:
        | 'EU'
        | 'US';
};

export const Logo = {
    Superbet: 'superbet',
    Napoleon: 'napoleon',
    SuperSocial: 'supersocial',
} as const;
export type Logo = typeof Logo[keyof typeof Logo];

export type Footer = {
    viberNumber: string;
    hasLegalSectionTitle: boolean;
    hasAgeWarning: boolean;
    hasDisclaimer: boolean;
    hasOnjnLogo: boolean;
    hasRombetLogo: boolean;
    hasIbiaLogo: boolean;
    hasJocResponsabilLogo: boolean;
    hasBigLogos: boolean;
    ageLimit: string;
    hasAgeLimitCrossed: boolean;
    hasAgeLimitNoLink: boolean;
    hasMgaLogo: boolean;
    hasBrazilGovLogo: boolean;
    hasBagoLogo: boolean;
    hasFpsLogo: boolean;
    hasGamblingHelpLogo: boolean;
    hasAlwaysPlayLegallyLogo: boolean;
    hasPlayResponsiblyText: boolean;
    hasGamblingTherapyLogo: boolean;
    hasResponsibleGamingLinks: boolean;
    shouldPromptKycOnLogin?: boolean;
    isOnlyKycPendingStatus?: boolean;
};

export type Header = {
    isBalanceShown: boolean;
    hasNotificationsIcon: boolean;
    accountMenuAuthenticated?: AccountMenuAuthenticated;
};

export type AccountMenuAuthenticated = {
    isRedirect: boolean;
    redirectRouteName?: string;
};

export type Help = {
    canOpenChatViaUrlHash: boolean;
    isChatEnabled: boolean;
};

export type SdsPaymentLogo = {
    logoName: string;
    width?: number;
    height?: number;
};

export type Deposit = {
    isAcceptingTermMandatory?: true;
    areMultipleOnlineMethods?: true;
    hasDepositLimitReachedModal?: true;
    paymentLogos?: {
        online?: SdsPaymentLogo[]; // Nuvei in Romania, Belgium, and Poland - Monri in Serbia
        onlinePix?: SdsPaymentLogo[]; // Only Brazil
        bancontact?: SdsPaymentLogo[]; // Only Belgium
        aircash?: SdsPaymentLogo[]; // Only Romania
        paySafeCard?: SdsPaymentLogo[]; // Only Romania
        onlinePiq?: SdsPaymentLogo[]; // Only Romania
    }
};

export type Withdrawal = {
    paymentLogos?: {
        onlinePix?: SdsPaymentLogo[]; // Only Brazil
        paySafeCard?: SdsPaymentLogo[]; // Only Romania
    }
};

export type BonusWallet = {
    awardFreeSpinsByCount: boolean;
    categories: Record<BonusWalletCategory, boolean>;
};

export const BonusWalletCategory = {
    Casino: 'Casino',
    Virtuals: 'Virtuals',
    CardGames: 'CardGames',
    Sport: 'Sport',
    Retail: 'Retail',
    Bingo: 'Bingo',
} as const;
export type BonusWalletCategory = typeof BonusWalletCategory[keyof typeof BonusWalletCategory];

export const ResponsibleGamingPageElement = {
    DepositLimit: 'DepositLimit',
    TotalWagerLimit: 'TotalWagerLimit',
    TimeOut: 'TimeOut',
    AccountClosure: 'AccountClosure',
    SelfExclusion: 'SelfExclusion',
    LossLimit: 'LossLimit',
    SessionLimit: 'SessionLimit',
    RealityCheck: 'RealityCheck',
} as const;

export type ResponsibleGamingPageElement =
    typeof ResponsibleGamingPageElement[keyof typeof ResponsibleGamingPageElement];

export type MenuItem = {
    id: string;
    name: string;
    icon: string;
};

export type BankAccountConfig = Partial<{
    length: number;
    whitelistedPrefix: string[];
    allowedCharactersRegex: string;
    ibanFormateRegex: string;
}>;

export const backAccountMenuItemId = 'back';

export type DepositLimit = {
    isDeletable: boolean;
    monthAllowed: boolean;
    weekAllowed: boolean;
    dayAllowed: boolean;
    hasWeeklyDepositLimitModal?: boolean;
};

export type LossLimit = {
    isDeletable: boolean;
    monthAllowed: boolean;
    weekAllowed: boolean;
    dayAllowed: boolean;
};

export type SessionLimit = {
    isDeletable: boolean;
    monthAllowed: boolean;
    weekAllowed: boolean;
    dayAllowed: boolean;
};

export type SportOfferSort = {
    getEventSortRules: () => Array<Comparator<OfferEvent>>;
};

export type SelectableLocale = {
    name: string;
    value: Locale;
    flag: string;
};

export type FreeToPlay = {
    useEligibilityChecker: boolean;
};

type Bonus = {
    isPromoRewardVisible?: boolean;
    vipUrl?: string;
    isPromotionHubEntryPointVisible: boolean;
    excludedBonusTypes?: Array<BonusSubtypeDto | BonusTypeComtrade>;
    availablePromoHubTabs?: Array<PromotionHubTab>;
};

export enum SiteLingualityKind {
    SingleLingual = 'SingleLingual',
    MultiLingual = 'MultiLingual',
}

type SingleLingual = {
    kind: SiteLingualityKind.SingleLingual;
    locales: SelectableLocale[];
};

type MultiLingual = {
    kind: SiteLingualityKind.MultiLingual;
    locales: SelectableLocale[];
    hasLocalesInUrls: boolean;
};

type SeonConfig = {
    isComtradeFlowSupported?: boolean;
    login: {
        isServiceEnabled: boolean;
    },
    register: {
        isServiceEnabled: boolean;
    },
    transactions: {
        isServiceEnabled: boolean;
        shouldSendWithTransaction: boolean;
        shouldSendSeparateCall: boolean;
    },
};
